import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectClient, putMultipleFundingSourceForClientByClientIdRequest } from '@client/model';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { useAllAllocations } from '@/features/guided-financial-planning/stores';
import type { PlanComparisonResult } from './use-plan-comparison-scenarios';

/**
 * Build a snapshot from the current plan comparison result.
 * This captures all the metrics needed to display the plan in read-only mode.
 */
function buildPlanComparisonSnapshot(planComparison: PlanComparisonResult): PlanComparisonSnapshot {
  return {
    projectedLtcCost: planComparison.projectedLtcCost,
    withoutInsurance: { ...planComparison.withoutInsurance },
    withInsurance: { ...planComparison.withInsurance },
    selectedPolicy: planComparison.selectedPolicy ? { ...planComparison.selectedPolicy } : null,
    fundingAllocations: planComparison.fundingAllocations.map(a => ({ ...a })),
    gapReduction: planComparison.gapReduction,
    netBenefit: planComparison.netBenefit,
    snapshotTimestamp: new Date().toISOString(),
  };
}

/**
 * Hook to finalize a guided planning session.
 *
 * This hook handles the complete finalization flow:
 * 1. Snapshots the plan comparison metrics for read-only mode
 * 2. Persists all selected quotes to the client's funding sources in the database
 * 3. Marks the session as completed
 *
 * The policy is only persisted when the session is finalized, not when the quote is selected.
 */
export function useFinalizeSession() {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { session } = useSession();
  const { updateStepData, completeSession: markSessionComplete } = useSessionMutations();
  const allAllocations = useAllAllocations();
  const [isFinalizing, setIsFinalizing] = useState(false);

  /**
   * Persist all selected quotes to the client's funding sources.
   */
  const persistSelectedQuotes = useCallback(async (): Promise<void> => {
    if (!client?.clientId) return;

    const selectedQuotes = (session?.stepData?.selectedQuotes ?? {}) as Record<
      string,
      SelectedQuoteData
    >;

    for (const [, quoteData] of Object.entries(selectedQuotes)) {
      const { policyFundingSource } = quoteData;
      const { pendingFundingSourceId, ...policyToSave } = policyFundingSource;

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: policyToSave,
          fundingSourceId: pendingFundingSourceId,
        }),
      );
    }
  }, [client?.clientId, session?.stepData?.selectedQuotes, dispatch]);

  /**
   * Apply funding deductions to self-funding sources based on user allocations.
   */
  const applyFundingDeductions = useCallback(async (): Promise<void> => {
    if (!client?.clientId) return;

    const selfFundingSources = client.multipleFundingSources?.selfFunding ?? {};
    const selectedQuotes = session?.stepData?.selectedQuotes ?? {};
    const selectedQuoteId = Object.keys(selectedQuotes)[0];
    if (!selectedQuoteId) return;

    const allocations = allAllocations[selectedQuoteId] ?? {};

    const updates: Array<{ fundingSourceId: string; existingAssetsValue: number }> = [];

    for (const [sourceId, allocatedAmount] of Object.entries(allocations)) {
      if (!allocatedAmount || allocatedAmount <= 0) continue;

      const source = selfFundingSources[sourceId];
      if (!source) continue;

      const originalValue = source.existingAssetsValue ?? 0;
      const newValue = Math.max(0, originalValue - allocatedAmount);

      updates.push({ fundingSourceId: sourceId, existingAssetsValue: newValue });
    }

    if (updates.length === 0) return;

    await guidedPlanningApi.batchUpdateSelfFundingAssets({
      clientId: client.clientId,
      updates,
    });
  }, [
    client?.clientId,
    client?.multipleFundingSources?.selfFunding,
    session?.stepData?.selectedQuotes,
    allAllocations,
  ]);

  /**
   * Finalize the session: snapshot metrics, persist policies, apply deductions, and mark session as completed.
   * @param planComparison - The current plan comparison result to snapshot
   */
  const finalizeSession = useCallback(
    async (planComparison: PlanComparisonResult) => {
      if (session?.status === 'completed') {
        return undefined;
      }

      setIsFinalizing(true);

      try {
        // 1. Snapshot the plan comparison metrics and funding sources for read-only mode
        const snapshot = buildPlanComparisonSnapshot(planComparison);
        const multipleFundingSourcesSnapshot = client?.multipleFundingSources
          ? (JSON.parse(JSON.stringify(client.multipleFundingSources)) as MultipleFundingSources)
          : undefined;
        await updateStepData({ planComparisonSnapshot: snapshot, multipleFundingSourcesSnapshot });

        // 2. Persist all selected quotes to the database
        await persistSelectedQuotes();

        // 3. Apply funding deductions to self-funding sources
        await applyFundingDeductions();

        // 4. Mark the session as completed
        return markSessionComplete();
      } finally {
        setIsFinalizing(false);
      }
    },
    [
      session?.status,
      client?.multipleFundingSources,
      updateStepData,
      persistSelectedQuotes,
      applyFundingDeductions,
      markSessionComplete,
    ],
  );

  return {
    finalizeSession,
    isFinalizing,
  };
}
